import { diagnosticsService } from './diagnosticsService';

const DIALOG_SELECTOR = '[role="dialog"], [role="alertdialog"]';

let observer: MutationObserver | null = null;
// Tracks currently-mounted dialog elements in insertion order so we can
// report the topmost one when multiple are open.
const trackedDialogs = new Map<Element, string>();

function getDialogName(el: Element): string {
    const labelledBy = el.getAttribute('aria-labelledby');
    if (labelledBy) {
        const label = document.getElementById(labelledBy);
        const text = label?.textContent?.trim();
        if (text) return text;
    }
    const heading = el.querySelector('h1, h2, h3, h4, h5, h6, [class*="DialogTitle"]');
    const headingText = heading?.textContent?.trim();
    if (headingText) return headingText;
    return 'Unnamed dialog';
}

function reportTopmost(): void {
    if (trackedDialogs.size === 0) {
        diagnosticsService.setDialog(null);
    } else {
        const names = Array.from(trackedDialogs.values());
        diagnosticsService.setDialog(names[names.length - 1]);
    }
}

function handleAddedNode(node: Node) {
    if (!(node instanceof Element)) return;
    const dialogs = node.matches?.(DIALOG_SELECTOR)
        ? [node]
        : Array.from(node.querySelectorAll?.(DIALOG_SELECTOR) ?? []);
    let changed = false;
    for (const el of dialogs) {
        if (!trackedDialogs.has(el)) {
            trackedDialogs.set(el, getDialogName(el));
            changed = true;
        }
    }
    if (changed) reportTopmost();
}

function handleRemovedNode(node: Node) {
    if (!(node instanceof Element)) return;
    let changed = false;
    for (const el of Array.from(trackedDialogs.keys())) {
        if (el === node || node.contains?.(el)) {
            trackedDialogs.delete(el);
            changed = true;
        }
    }
    if (changed) reportTopmost();
}

export function startDialogTracking(): void {
    if (observer) return;
    observer = new MutationObserver((mutations) => {
        for (const m of mutations) {
            m.addedNodes.forEach(handleAddedNode);
            m.removedNodes.forEach(handleRemovedNode);
        }
    });
    observer.observe(document.body, { childList: true, subtree: true });
}

export function stopDialogTracking(): void {
    observer?.disconnect();
    observer = null;
    trackedDialogs.clear();
}
